import { ChevronDown, ChevronUp } from 'lucide-react';
import { useMemo, useState } from 'react';

import Button from '@/components/Button/Button';
import Typo from '@/components/Typography/Typo';
import { SourceScraperMap } from '@/features/Scraping/configs';
import { getSourceByScraperNameSafe } from '@/features/Scraping/configs/config-actions';
import useColor from '@/hooks/domain/useColor';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import { cn } from '@/lib/cn';
import useFeedbackSourceStore from '@/stores/Feedback/FeedbackSourceStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import { Space } from '../../../components/base';
import { SourceMapper } from './components/SourceMapper';

/* NOTE:
  - 'FeedbackSource' indicates a type of 3rd party apps that can be source of feedbacks - e.g. Slack, Channel, etc.
*/

enum RenderListItemType {
  SOURCE = 'source',
  HEADER = 'header',
}

type RenderListItem =
  | {
      type: RenderListItemType.SOURCE;
      source: SOURCE_TYPE;
    }
  | {
      type: RenderListItemType.HEADER;
      header: string;
    };

const CUSTOMER_SUPPORT_NAMES = [
  SOURCE_TYPE.INTERCOM,
  SOURCE_TYPE.ZENDESK,
  SOURCE_TYPE.FRONT,
  SOURCE_TYPE.SLACK,
  SOURCE_TYPE.GORGIAS,
  SOURCE_TYPE.CHANNELTALK,
  SOURCE_TYPE.KUSTOMER,
  SOURCE_TYPE.FRESHDESK,
  SOURCE_TYPE.ATLAS,
  SOURCE_TYPE.HELPSCOUT,
  SOURCE_TYPE.RICH_PANEL,
];

const REVIEW_NAMES = [
  SOURCE_TYPE.GOOGLE_PLAY,
  SOURCE_TYPE.APPLE_APP_STORE,
  SOURCE_TYPE.G2,
  SOURCE_TYPE.STAMPED,
  SOURCE_TYPE.YOTPO,
  SOURCE_TYPE.OKENDO,
  SOURCE_TYPE.TRUSTPILOT,
];

const CALL_NAMES = [SOURCE_TYPE.GONG];

const COMMUNITY_NAMES = [SOURCE_TYPE.DISCORD, SOURCE_TYPE.FACEBOOK];

const SURVEY_NAMES = [
  SOURCE_TYPE.TYPEFORM,
  SOURCE_TYPE.KNOCOMMERCE,
  SOURCE_TYPE.QUALTRICS,
];

const CRM_NAMES = [SOURCE_TYPE.SALESFORCE];

const IntegrationPlatformTab = () => {
  const wr = useWorkspaceRouter();
  const color = useColor();
  const sourceInfo = useFeedbackSourceStore((state) => state.sourceInfo);
  const scraperNames = useWorkspaceStore(
    (state) => state.currWorkspace?.config.scraper_names,
  );
  const connectedSources = useMemo(() => {
    const configuredSources = new Set(
      scraperNames?.map(getSourceByScraperNameSafe),
    );
    return (sourceInfo ?? []).filter(
      (item) =>
        !SourceScraperMap.has(item.source) || configuredSources.has(item.source),
    );
  }, [sourceInfo, scraperNames]);
  const hasNoSource = connectedSources.length === 0;
  const [showAll, setShowAll] = useState(hasNoSource);

  const renderList = useMemo(() => {
    let list: RenderListItem[] = [];

    const filterExistSource = (sources: SOURCE_TYPE[]): SOURCE_TYPE[] => {
      return sources.filter(
        (source) => !connectedSources.some((s) => s.source === source),
      );
    };

    if (!hasNoSource) {
      list.push({
        type: RenderListItemType.HEADER,
        header: 'Connected',
      });

      connectedSources.forEach((item) => {
        list.push({
          type: RenderListItemType.SOURCE,
          source: item.source,
        });
      });
    }

    list.push({
      type: RenderListItemType.HEADER,
      header: 'Customer support',
    });
    filterExistSource(CUSTOMER_SUPPORT_NAMES).forEach((source) => {
      list.push({
        type: RenderListItemType.SOURCE,
        source: source,
      });
    });
    list.push({
      type: RenderListItemType.HEADER,
      header: 'Reviews',
    });
    filterExistSource(REVIEW_NAMES).forEach((source) => {
      list.push({
        type: RenderListItemType.SOURCE,
        source: source,
      });
    });
    list.push({
      type: RenderListItemType.HEADER,
      header: 'Call recordings',
    });
    filterExistSource(CALL_NAMES).forEach((source) => {
      list.push({
        type: RenderListItemType.SOURCE,
        source: source,
      });
    });

    list.push({
      type: RenderListItemType.HEADER,
      header: 'Community / Social media',
    });
    filterExistSource(COMMUNITY_NAMES).forEach((source) => {
      list.push({
        type: RenderListItemType.SOURCE,
        source: source,
      });
    });

    list.push({
      type: RenderListItemType.HEADER,
      header: 'Surveys',
    });
    filterExistSource(SURVEY_NAMES).forEach((source) => {
      list.push({
        type: RenderListItemType.SOURCE,
        source: source,
      });
    });

    list.push({
      type: RenderListItemType.HEADER,
      header: 'CRM',
    });
    filterExistSource(CRM_NAMES).forEach((source) => {
      list.push({
        type: RenderListItemType.SOURCE,
        source: source,
      });
    });

    return list;
  }, [hasNoSource, connectedSources]);

  const connectedSectionEndIndex = useMemo(() => {
    const sourceLen = connectedSources.length;
    return sourceLen + 1;
  }, [connectedSources]);

  return (
    <div className="flex flex-1 flex-col overflow-auto">
      <div className="p-8 flex flex-col gap-2">
        {renderList.map((item, index) => {
          const shouldHide =
            !showAll &&
            connectedSectionEndIndex !== -1 &&
            index >= connectedSectionEndIndex;
          if (item.type === RenderListItemType.HEADER) {
            return (
              <Typo
                key={item.header}
                type="label2"
                className={cn(`pt-3 pb-1`, shouldHide && 'hidden')}
                weight="sb"
                color={color.gray700}
              >
                {item.header}
              </Typo>
            );
          }
          return (
            <div key={item.source} className={cn(shouldHide && 'hidden')}>
              <SourceMapper
                key={item.source}
                source={item.source}
                item={connectedSources.find((s) => s.source === item.source)}
                onScraperSelectCallback={(scraper) => {
                  wr.push(`/settings/integration/scraping`, {
                    state: {
                      scraper,
                    },
                  });
                }}
              />
            </div>
          );
        })}
      </div>
      <div className="px-8">
        {!hasNoSource && (
          <Button
            variant="secondary"
            sizeType="m"
            className="w-full"
            leftIcon={showAll ? <ChevronUp /> : <ChevronDown />}
            onClick={() => setShowAll((prev) => !prev)}
          >
            {showAll ? 'Hide platforms' : 'Show all platforms'}
          </Button>
        )}
      </div>
      <Space h="32px" />
    </div>
  );
};

export default IntegrationPlatformTab;
